import { useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
// @mui
import {
  Button,
  Stack,
  Container,
  Typography,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  DialogActions,
  IconButton,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
// components
import Iconify from '../../../components/Iconify';
import MeetingScheduler from '../../../components/meeting/MeetingScheduler';
// hooks
import useAuth from '../../../hooks/useAuth';
import useResponsive from '../../../hooks/useResponsive';
// swr
import { mutate } from 'swr';
// utils
import axios from '../../../utils/axios';
import { fKrMdEKST } from '../../../utils/formatTime';

// ----------------------------------------------------------------------

export default function MeetingConfirmDialog({ isOpen, closeDialog, coffeeChat }) {
  const smUp = useResponsive('up', 'sm');
  const { user } = useAuth();

  const { enqueueSnackbar } = useSnackbar();
  const [notAvailableSchedule, setNotAvailableSchedule] = useState(false);
  const [timeComment, setTimeComment] = useState('');
  const [timeCommentEmpty, setTimeCommentEmpty] = useState(false);
  const [timeCommentOverLength, setTimeCommentOverLength] = useState(false);
  const [isSubmit, setIsSubmit] = useState(false);

  const getNextDays = (date = new Date()) => {
    const DAY_OF_FRIDAY = 5;
    const dateCopy = new Date(date.getTime());
    const addWeek = dateCopy.getDay() > DAY_OF_FRIDAY ? 0 : 7;
    const dayToNextDay = (7 - dateCopy.getDay() + DAY_OF_FRIDAY) % 7;
    const nextFriday = new Date(dateCopy.setDate(
      dateCopy.getDate() + addWeek + dayToNextDay
    ));

    const nextMonday = new Date(nextFriday);
    nextMonday.setDate(nextMonday.getDate() - 4);

    const nextWednesday = new Date(nextFriday);
    nextWednesday.setDate(nextWednesday.getDate() - 2);

    return {nextMonday, nextWednesday, nextFriday};
  }

  const { nextMonday, nextWednesday, nextFriday } = getNextDays();
  const reScheduleLimit = fKrMdEKST(nextFriday);
  const toMonthStr = nextMonday.getMonth() !== nextWednesday.getMonth() ? `${nextWednesday.getMonth()+1}월 ` : ''
  const notAvailableSchedulePlaceholder = `예) ${nextMonday.getMonth()+1}월 ${nextMonday.getDate()}일 ~ ${toMonthStr}${nextWednesday.getDate()}일에 커피챗 가능하신 다른 시간을 알려주세요!`;

  const handleTimeComment = (e) => {
    setTimeComment(e.target.value);
    setTimeCommentEmpty(!e.target.value);
    setTimeCommentOverLength(e.target.value.length > 100);
  };

  const openRequestChatDialog = () => {
    setNotAvailableSchedule(true);
  };

  const closeRequestChatDialog = () => {
    setNotAvailableSchedule(false);
    setTimeComment('');
    setTimeCommentEmpty(false);
    setTimeCommentOverLength(false);
  };

  const reload = () => {
    mutate(`/api/company/${user?.employee.company[0].id}/fliers/coffee/`);
    closeDialog();
  }

  const requestOtherTimes = async () => {
    if (!timeComment) {
      setTimeCommentEmpty(true);
      return ;
    }
    if (timeComment.length > 100) {
      setTimeCommentOverLength(true);
      return ;
    }
    try {
      setIsSubmit(true);
      const companyId = user?.employee.company[0].id;
      const coffeeChatId = coffeeChat?.id;
      await axios.post(
        `/api/company/${companyId}/fliers/coffee/${coffeeChatId}/request-other-times/`,
        {time_comment: timeComment}
      );
      enqueueSnackbar('커피챗 시간 변경 요청을 보냈습니다');
      setNotAvailableSchedule(false);
      reload();
      setIsSubmit(false);
    } catch (error) {
      setIsSubmit(false);
      console.error(error);
      enqueueSnackbar('커피챗 시간 변경 요청에 실패하였습니다. 관리자에 문의해주세요', { variant: 'error' });
    }
  };

  const handleMeetingScheduled = () => {
    reload();
  }

  useEffect(() => {
    if (!isOpen) {
      setTimeComment('');
      setTimeCommentEmpty(false);
      setTimeCommentOverLength(false);
    }
  }, [isOpen]);

  return (
    <>
      <Dialog fullScreen open={isOpen} onClose={closeDialog}>
        <DialogContent>
          <Container
            maxWidth="laptop"
            sx={{ p:1, mb: { xs: 0, sm: 1 } }}
          >
            <Stack direction="row" sx={{ justifyContent: 'flex-end' }}>
              <IconButton edge={"end"} onClick={closeDialog}>
                <Iconify icon="eva:close-fill" sx={{ width: 32, height: 32, color: 'text.primary' }} />
              </IconButton>
            </Stack>
            <Grid
              maxWidth="laptop"
              container
              sx={{ p:1, direction: 'row', justifyContent: 'space-between', alignItems: 'flex-end' }}
            >
              <div>
                <Stack>
                  <Typography variant="h4" gutterBottom>
                    크루와 커피챗을 진행할 날짜/시간을 선택해주세요.
                  </Typography>
                  <Typography variant="body1">
                    아래 달력에는 크루가 커피챗 가능한 시간이 표시되어 있습니다.
                  </Typography>
                </Stack>
              </div>
              <div>
                {smUp && (
                  <Button variant="outlined" onClick={openRequestChatDialog}>
                    다른 커피챗 가능 시간 요청하기
                  </Button>
                )}
              </div>
            </Grid>
          </Container>
          {!smUp && (
          <Container sx={{textAlign: 'center', mb: 1 }}>
            <Button size="small" variant="outlined" onClick={openRequestChatDialog}>
              다른 커피챗 가능 시간 요청하기
            </Button>
          </Container>
          )}
          <Container
            maxWidth="laptop"
            sx={{ p: 1, minWidth: '320px' }}
          >
            <MeetingScheduler
              calendarCode={coffeeChat?.calendar_code}
              user={user}
              onComplete={handleMeetingScheduled}
              title=""
              description={""}
              duration={40}
              showTimezone={true}
            />
          </Container>
        </DialogContent>

        <Dialog open={notAvailableSchedule} fullWidth maxWidth="xs" onClose={closeRequestChatDialog}>
          <DialogTitle sx={{ px: 4, pt: 4, pb: 1 }}>달력에 커피챗 가능한 시간이 없나요?</DialogTitle>
          <DialogContent sx={{ px: 4, py: 0 }}>
            <Stack sx={{ mt: 1, mb: 2 }}>
              <Typography gutterBottom>
                다른 시간을 요청해주시면 크루에게 전달해드릴게요.
              </Typography>
              <Stack direction="row" spacing={0.5} alignItems="center">
                <Iconify icon={"eva:alert-triangle-fill"} sx={{ width: 20, height: 20, color: 'error.main' }} />
                <Typography variant="subtitle1" sx={{ color: 'error.main', wordBreak: "keep-all" }}>
                  {reScheduleLimit} 이전, 평일 19시 전으로 요청해주세요.
                </Typography>
              </Stack>
              <Typography variant="body1" sx={{ ml: { xs: 2.5, sm: 3.5 }, color: 'error.main', wordBreak: "keep-all" }}>
                주말은 예약이 불가능합니다.
              </Typography>
            </Stack>
            <TextField
              fullWidth
              multiline
              minRows={3}
              value={timeComment}
              onChange={handleTimeComment}
              placeholder={notAvailableSchedulePlaceholder}
              error={timeCommentEmpty || timeCommentOverLength}
              helperText={timeCommentEmpty ? '내용을 입력해주세요' : timeCommentOverLength ? '100자 이내로 입력해주세요' : ''}
            />
          </DialogContent>
          <DialogActions sx={{ m: 1 }}>
            <LoadingButton
              size="large"
              sx={{ px: 5 }}
              onClick={closeRequestChatDialog}
              loading={isSubmit}
            >
              취소
            </LoadingButton>
            <LoadingButton
              variant="contained"
              size="large"
              sx={{ px: 5 }}
              onClick={requestOtherTimes}
              loading={isSubmit}
              disabled={timeCommentEmpty || timeCommentOverLength}
            >
              요청하기
            </LoadingButton>
          </DialogActions>
        </Dialog>
      </Dialog>
    </>
  );
};